import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Input } from './ui/input';
import { Button } from './ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';

const fmt = (v, unit) => `${Number(v || 0).toFixed(unit === 'liters' ? 1 : 0)}${unit === 'liters' ? ' L' : ''}`;

const CountHistory = ({ counts, L }) => (
  <div className="p-5 border-t border-slate-100" data-testid="inventory-history">
    <h4 className="font-semibold text-slate-800 mb-2 text-sm">{L('Previous counts', 'Bisherige Inventuren')}</h4>
    {counts.length === 0 && <p className="text-xs text-slate-400">{L('None yet', 'Noch keine')}</p>}
    <div className="space-y-1.5">
      {counts.slice(0, 10).map(c => (
        <details key={c.id} className="bg-slate-50 rounded-lg px-3 py-2 text-xs">
          <summary className="cursor-pointer flex flex-wrap justify-between gap-2">
            <span className="font-semibold text-slate-700">{new Date(c.created_at).toLocaleString('de-CH', { dateStyle: 'short', timeStyle: 'short' })} · {c.created_by_name}</span>
            <span className={c.items_with_difference ? 'text-rose-600 font-semibold' : 'text-emerald-600 font-semibold'}>
              {c.items_counted} {L('items', 'Artikel')} · {c.items_with_difference} {L('with difference', 'mit Differenz')}
            </span>
          </summary>
          <div className="mt-2 space-y-0.5">
            {c.lines.filter(l => l.difference).map(l => (
              <p key={l.item_id} className="flex justify-between"><span>{l.name}</span><span className="font-semibold text-rose-600">{l.difference > 0 ? '+' : ''}{fmt(l.difference, l.unit)}</span></p>
            ))}
            {c.note && <p className="text-slate-500 italic mt-1">{c.note}</p>}
          </div>
        </details>
      ))}
    </div>
  </div>
);

export const StockCount = ({ API, L, onDone }) => {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [history, setHistory] = useState([]);
  const [note, setNote] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    const [a, b] = await Promise.all([API.get('/cash-register/inventory-count/items'), API.get('/cash-register/inventory-counts')]);
    setItems(a.data); setHistory(b.data);
  }, [API]);
  useEffect(() => { load().catch(() => toast.error(L('Error loading', 'Fehler beim Laden'))); }, [load, L]);

  const entered = useMemo(() => items.filter(i => counts[i.item_id] !== undefined && counts[i.item_id] !== ''), [items, counts]);
  const diffs = entered.filter(i => Math.abs(parseFloat(counts[i.item_id]) - i.expected) > 0.001);

  const submit = async () => {
    setSaving(true);
    try {
      await API.post('/cash-register/inventory-count', {
        lines: entered.map(i => ({ item_id: i.item_id, counted: parseFloat(counts[i.item_id]) })),
        note: note || null,
      });
      toast.success(L('Stock count saved', 'Inventur abgeschlossen — Bestände aktualisiert'));
      setCounts({}); setNote(''); setConfirmOpen(false);
      await load(); onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || L('Error', 'Fehler'));
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white border border-slate-200/60 rounded-2xl shadow-sm" data-testid="stock-count">
      <div className="p-5 border-b border-slate-100 flex flex-wrap justify-between items-end gap-3">
        <div>
          <h3 className="font-bold text-slate-900">{L('Stock count', 'Inventur')}</h3>
          <p className="text-xs text-slate-500">{L('Enter the counted quantity per item. Empty rows are not changed.', 'Gezählten Bestand pro Artikel eintragen. Leere Zeilen bleiben unverändert.')}</p>
        </div>
        <div className="flex items-center gap-3 text-sm">
          <span className="text-slate-500" data-testid="count-progress">{entered.length}/{items.length} {L('counted', 'gezählt')}</span>
          <span className={`font-semibold ${diffs.length ? 'text-rose-600' : 'text-emerald-600'}`} data-testid="count-diff-count">{diffs.length} {L('differences', 'Differenzen')}</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead className="bg-slate-50 text-[11px] uppercase text-slate-500">
            <tr>
              <th className="text-left px-4 py-2">{L('Item', 'Artikel')}</th>
              <th className="text-right px-3 py-2">{L('Expected', 'Soll (System)')}</th>
              <th className="text-right px-3 py-2 w-36">{L('Counted', 'Gezählt')}</th>
              <th className="text-right px-4 py-2">{L('Difference', 'Differenz')}</th>
            </tr>
          </thead>
          <tbody>
            {items.map(i => {
              const v = counts[i.item_id];
              const has = v !== undefined && v !== '';
              const d = has ? parseFloat(v) - i.expected : 0;
              const bad = has && Math.abs(d) > 0.001;
              return (
                <tr key={i.item_id} className={`border-t border-slate-100 ${bad ? 'bg-rose-50/60' : ''}`} data-testid={`count-row-${i.item_id}`}>
                  <td className="px-4 py-2"><p className="font-semibold text-slate-800">{i.name}</p><p className="text-[11px] text-slate-400">{i.category}</p></td>
                  <td className="text-right px-3 py-2 text-slate-600">{fmt(i.expected, i.unit)}</td>
                  <td className="px-3 py-1.5">
                    <Input type="number" min="0" step={i.unit === 'liters' ? '0.1' : '1'} inputMode="decimal" value={v ?? ''}
                      onChange={e => setCounts(c => ({ ...c, [i.item_id]: e.target.value }))}
                      className="h-9 text-right bg-slate-50" data-testid={`count-input-${i.item_id}`} />
                  </td>
                  <td className={`text-right px-4 py-2 font-bold ${!has ? 'text-slate-300' : bad ? 'text-rose-600' : 'text-emerald-600'}`} data-testid={`count-diff-${i.item_id}`}>
                    {has ? `${d > 0 ? '+' : ''}${fmt(d, i.unit)}` : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="p-5 border-t border-slate-100 flex flex-wrap gap-3 items-center">
        <Input value={note} onChange={e => setNote(e.target.value)} placeholder={L('Note (optional)', 'Notiz (optional), z.B. Monatsinventur Oktober')} className="flex-1 min-w-[200px] h-10 bg-slate-50" data-testid="count-note" />
        <Button disabled={!entered.length || saving} onClick={() => setConfirmOpen(true)} className="h-10 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold" data-testid="count-submit">
          {L('Finish stock count', 'Inventur abschliessen')}
        </Button>
      </div>
      <CountHistory counts={history} L={L} />
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-white" data-testid="count-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{L('Finish stock count?', 'Inventur wirklich abschliessen?')}</AlertDialogTitle>
            <AlertDialogDescription>
              {L(`${entered.length} items will be set to the counted quantity (${diffs.length} with difference).`, `${entered.length} Artikel werden auf den gezählten Bestand gesetzt (${diffs.length} mit Differenz).`)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="count-confirm-cancel">{L('Cancel', 'Abbrechen')}</AlertDialogCancel>
            <AlertDialogAction onClick={(e) => { e.preventDefault(); submit(); }} disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="count-confirm-yes">
              {saving ? '…' : L('Yes, finish', 'Ja, abschliessen')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
